import {
  FilterGroup,
  createFilterGroup as createMutation,
  updateFilterGroup as updateMutation,
  CreateFilterGroupMutationVariables,
  CreateFilterGroupInput,
  UpdateFilterGroupInput,
  cleanInputCreate,
  getReadId,
  getTableClientId,
  FILTER_GROUP_TABLE_NAME,
  cleanInputUpdate,
  DeleteFilterGroupMutationVariables as DeleteMutationVariables,
  deleteFilterGroup as deleteMutation,
  getFilterGroup as getQuery,
  syncFilterGroups,
  OnCreateFilterGroupSubscription,
  OnUpdateFilterGroupSubscription,
  OnDeleteFilterGroupSubscription,
  onCreateFilterGroup,
  onUpdateFilterGroup,
  onDeleteFilterGroup,
  FilterGroupType,
} from '@rentguru/commons-utils';
import {
  deleteEntityWithFetchBefore,
  getCacheItem,
  list,
  mutation,
  setCacheItem,
  useSubscriptions,
} from '@up2rent/graphql';
import compact from 'lodash/compact';
import isNil from 'lodash/isNil';
import { createContext, useReducer, useContext, useEffect, useMemo, ReactNode, useState } from 'react';

import { MANAGEMENT_FILTERS_CACHE } from 'src/utils/cacheKeys';

import { useUser } from './UserContext';
import { useUsers } from './UsersContext';

interface State {
  filterGroups: FilterGroup[] | null;
  loading: boolean;
  shouldFetch: boolean;
}

type Action =
  | { type: 'SHOULD_FETCH' }
  | { type: 'IS_FETCHING' }
  | { type: 'FETCHED'; payload: FilterGroup[] }
  | { type: 'FETCH_FAILED' }
  | { type: 'ADD'; payload: FilterGroup }
  | { type: 'UPDATE'; payload: FilterGroup }
  | { type: 'DELETE'; payload: string };

const initialState: State = {
  filterGroups: null,
  loading: false,
  shouldFetch: true,
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'SHOULD_FETCH':
      if (state.loading || state.shouldFetch) return state;
      return { ...state, shouldFetch: true };
    case 'IS_FETCHING':
      return { ...state, loading: true };
    case 'FETCHED':
      return { ...state, loading: false, shouldFetch: false, filterGroups: action.payload };
    case 'FETCH_FAILED':
      return { ...state, loading: false, shouldFetch: false };
    case 'ADD':
      return { ...state, filterGroups: [...(state.filterGroups ?? []), action.payload] };
    case 'UPDATE':
      if (!state.filterGroups) return state;
      return {
        ...state,
        filterGroups: state.filterGroups.map((g) => (g.id === action.payload.id ? action.payload : g)),
      };
    case 'DELETE':
      if (!state.filterGroups) return state;
      return {
        ...state,
        filterGroups: state.filterGroups.filter((g) => g.id !== action.payload),
      };
    default:
      return state;
  }
};

export interface FilterGroupsContextState extends State {
  createFilterGroup: (input: Omit<CreateFilterGroupInput, 'clientId' | 'readId'>) => Promise<FilterGroup>;
  updateFilterGroup: (updates: UpdateFilterGroupInput) => Promise<FilterGroup>;
  deleteFilterGroup: (filterGroupToDeleteId: string) => Promise<FilterGroup>;
  getFilterGroupsByUserIdAndType: (userId: string, type: FilterGroupType) => FilterGroup[];
  selectManagementFilterGroup: (userId: string) => Promise<void>;
  unselectManagementFilterGroup: (userId: string) => Promise<void>;
  selectManagementFilterGroups: (userIds: string[]) => Promise<void>;
  setFetchFilterGroups: () => void;
  selectedManagementFilterGroups: FilterGroup[];
  selectedManagementFilterContactIds: string[];
}

const FilterGroupsContext = createContext<FilterGroupsContextState | null>(null);

export const FilterGroupsProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [selectedManagementFilterGroups, setSelectedManagementFilterGroups] = useState<FilterGroup[]>([]);
  const { clientId } = useUser();
  const { users } = useUsers();

  const setFetchFilterGroups = () => dispatch({ type: 'SHOULD_FETCH' });

  useEffect(() => {
    const load = async () => {
      dispatch({ type: 'IS_FETCHING' });
      try {
        const allFilterGroups = await list<FilterGroup>(
          syncFilterGroups,
          'clientId',
          getTableClientId(clientId!, FILTER_GROUP_TABLE_NAME)
        );
        const selectedUserIdsFromCache: string[] | undefined = await getCacheItem(MANAGEMENT_FILTERS_CACHE);
        setSelectedManagementFilterGroups(
          allFilterGroups.filter(
            (filterGroup) => filterGroup?.userId && selectedUserIdsFromCache?.includes(filterGroup?.userId)
          )
        );
        dispatch({ type: 'FETCHED', payload: allFilterGroups });
      } catch (error) {
        console.error('[filterGroups] fetching filter groups failed', error);
        dispatch({ type: 'FETCH_FAILED' });
      }
    };
    if (state.shouldFetch && clientId) load();
  }, [state.shouldFetch, clientId]);

  useSubscriptions<OnCreateFilterGroupSubscription, OnUpdateFilterGroupSubscription, OnDeleteFilterGroupSubscription>(
    onCreateFilterGroup,
    onUpdateFilterGroup,
    onDeleteFilterGroup,
    (data) => {
      dispatch({
        type: 'ADD',
        payload: data.onCreateFilterGroup as FilterGroup,
      });
    },
    (data) => {
      dispatch({
        type: 'UPDATE',
        payload: data.onUpdateFilterGroup as FilterGroup,
      });
    },
    (data) => {
      const { id } = data.onDeleteFilterGroup as FilterGroup;
      dispatch({
        type: 'DELETE',
        payload: id,
      });
    }
  );

  const createFilterGroup = async (input: Omit<CreateFilterGroupInput, 'clientId' | 'readId'>) => {
    const createdFilterGroup = await mutation<FilterGroup, CreateFilterGroupMutationVariables>(createMutation, {
      input: {
        ...(cleanInputCreate(input) as CreateFilterGroupInput),
        clientId: getTableClientId(clientId!, FILTER_GROUP_TABLE_NAME),
        readId: getReadId(clientId!, FILTER_GROUP_TABLE_NAME),
      },
    });
    return createdFilterGroup;
  };

  const updateFilterGroup = async (updates: UpdateFilterGroupInput) =>
    await mutation<FilterGroup>(updateMutation, {
      input: { ...cleanInputUpdate(updates, false) },
    });

  const deleteFilterGroup = async (filterGroupToDeleteId: string) =>
    await deleteEntityWithFetchBefore<FilterGroup, DeleteMutationVariables>(
      { id: filterGroupToDeleteId } as FilterGroup,
      getQuery,
      deleteMutation
    );

  const getFilterGroupsByUserIdAndType = (userId: string, type: FilterGroupType) =>
    (state.filterGroups ?? []).filter((filterGroup) => filterGroup.userId === userId && filterGroup.type === type);

  const selectManagementFilterGroup = async (userId: string) => {
    const managementFilterToAdd = state.filterGroups?.find(
      (filterGroup) => filterGroup.type === FilterGroupType.MANAGEMENT && filterGroup.userId === userId
    );
    if (managementFilterToAdd) {
      const newSelected = [...selectedManagementFilterGroups, managementFilterToAdd];
      setSelectedManagementFilterGroups(newSelected);
      const userIdsSelected = newSelected.map((filterGroup) => filterGroup.userId);
      await setCacheItem(MANAGEMENT_FILTERS_CACHE, userIdsSelected);
    }
  };

  const unselectManagementFilterGroup = async (userId: string) => {
    const newSelected = selectedManagementFilterGroups.filter((oldFilter) => oldFilter.userId !== userId);
    setSelectedManagementFilterGroups(newSelected);
    const userIdsSelected = newSelected.map((filterGroup) => filterGroup.userId);
    await setCacheItem(MANAGEMENT_FILTERS_CACHE, userIdsSelected);
  };

  const selectManagementFilterGroups = async (userIds: string[]) => {
    const newSelected = (state.filterGroups ?? []).filter(
      (filterGroup) =>
        filterGroup.type === FilterGroupType.MANAGEMENT &&
        !isNil(filterGroup.userId) &&
        userIds.includes(filterGroup.userId)
    );
    setSelectedManagementFilterGroups(newSelected);
    const userIdsSelected = newSelected.map((filterGroup) => filterGroup.userId);
    await setCacheItem(MANAGEMENT_FILTERS_CACHE, userIdsSelected);
  };

  const selectedManagementFilterContactIds = useMemo<string[]>(
    () =>
      compact(
        selectedManagementFilterGroups.map(
          (filterGroup) => users.find(({ id }) => id === filterGroup.userId)?.contactId
        )
      ),
    [selectedManagementFilterGroups, users]
  );

  const value = useMemo(
    () => ({
      ...state,
      selectedManagementFilterGroups,
      selectedManagementFilterContactIds,
      createFilterGroup,
      updateFilterGroup,
      deleteFilterGroup,
      getFilterGroupsByUserIdAndType,
      selectManagementFilterGroup,
      unselectManagementFilterGroup,
      selectManagementFilterGroups,
      setFetchFilterGroups,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state, selectedManagementFilterGroups, selectedManagementFilterContactIds]
  );

  return <FilterGroupsContext.Provider value={value}>{children}</FilterGroupsContext.Provider>;
};

export const useFilterGroups = (): FilterGroupsContextState => {
  const context = useContext(FilterGroupsContext);
  if (!context) throw new Error('useFilterGroups must be used within FilterGroupsProvider');
  return context;
};
